/**
 * Cliente OAuth Authorization Code + PKCE, para un cliente publico sin
 * backend (ADR OQ-2, opcion A; spec/05-security.md §4.2).
 *
 * Decisiones de seguridad que el tipo hace estructurales, no solo convencion:
 *  - OAuthPkceConfig no tiene campo `clientSecret`: es imposible configurar
 *    uno, y por tanto imposible enviarlo por error.
 *  - exchangeCodeForToken jamas devuelve un refresh_token, aunque la
 *    respuesta del servidor incluya uno: se descarta activamente y se
 *    informa mediante `rejectedRefreshToken` para que sea observable y
 *    verificable por test, nunca silencioso sin rastro.
 */

import { defaultRandomSource, type RandomSource } from "../core/ids.js";

export interface OAuthPkceConfig {
  readonly authorizationEndpoint: string;
  readonly tokenEndpoint: string;
  readonly clientId: string; // publico; OAuth para apps publicas no usa secreto de cliente
  readonly redirectUri: string;
  readonly scopes: readonly string[];
}

export interface PkcePair {
  readonly codeVerifier: string;
  readonly codeChallenge: string; // metodo S256
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  const base64 = btoa(binary);
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function getSubtle(): SubtleCrypto {
  const g = globalThis as { crypto?: Crypto };
  if (!g.crypto?.subtle) throw new Error("SubtleCrypto no disponible");
  return g.crypto.subtle;
}

export function generateState(random: RandomSource = defaultRandomSource): string {
  return toBase64Url(random.randomBytes(32));
}

export async function generatePkcePair(random: RandomSource = defaultRandomSource): Promise<PkcePair> {
  const codeVerifier = toBase64Url(random.randomBytes(32)); // 43 caracteres, dentro de RFC 7636 (43-128)
  const digest = await getSubtle().digest("SHA-256", new TextEncoder().encode(codeVerifier));
  const codeChallenge = toBase64Url(new Uint8Array(digest));
  return { codeVerifier, codeChallenge };
}

export function buildAuthorizationUrl(config: OAuthPkceConfig, state: string, codeChallenge: string): string {
  const url = new URL(config.authorizationEndpoint);
  url.searchParams.set("client_id", config.clientId);
  url.searchParams.set("redirect_uri", config.redirectUri);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", config.scopes.join(" "));
  url.searchParams.set("state", state);
  url.searchParams.set("code_challenge", codeChallenge);
  url.searchParams.set("code_challenge_method", "S256");
  return url.toString();
}

export class StateMismatchError extends Error {
  constructor() {
    super("El state devuelto no coincide con el de la solicitud original: posible CSRF sobre el flujo OAuth");
    this.name = "StateMismatchError";
  }
}

export class RedirectUriMismatchError extends Error {
  constructor(received: string) {
    super(`redirect_uri fuera de la lista exacta registrada: ${received}`);
    this.name = "RedirectUriMismatchError";
  }
}

/** Valida vinculacion solicitud/respuesta antes de intercambiar el codigo (spec/05-security.md §4.2). */
export function validateCallback(
  config: OAuthPkceConfig,
  expectedState: string,
  receivedState: string,
  receivedRedirectUri: string,
): void {
  if (receivedState !== expectedState) throw new StateMismatchError();
  const normReceived = normalizeRedirectUri(receivedRedirectUri);
  const normExpected = normalizeRedirectUri(config.redirectUri);
  if (normReceived !== normExpected) throw new RedirectUriMismatchError(receivedRedirectUri);
}

function normalizeRedirectUri(uri: string): string {
  try {
    const u = new URL(uri);
    const pathname = u.pathname.length > 1 && u.pathname.endsWith("/") ? u.pathname.slice(0, -1) : u.pathname;
    return `${u.origin}${pathname}`;
  } catch {
    return uri;
  }
}

export interface AccessTokenResult {
  readonly accessToken: string;
  readonly expiresAt: number; // epoch ms
  /** true si la respuesta del servidor incluia un refresh_token; se descarto activamente y nunca se expone. */
  readonly rejectedRefreshToken: boolean;
}

export interface FetchLike {
  (url: string, init: { method: string; headers: Record<string, string>; body: string }): Promise<{
    ok: boolean;
    status: number;
    json(): Promise<unknown>;
  }>;
}

interface TokenEndpointResponse {
  access_token?: string;
  expires_in?: number;
  refresh_token?: string;
}

export class TokenExchangeError extends Error {
  constructor(status: number) {
    super(`El intercambio de codigo por token fallo con estado HTTP ${status}`);
    this.name = "TokenExchangeError";
  }
}

/**
 * Intercambia el codigo de autorizacion por un token de acceso. No envia ni
 * puede enviar client_secret (no existe ese campo en OAuthPkceConfig).
 */
export async function exchangeCodeForToken(
  config: OAuthPkceConfig,
  code: string,
  codeVerifier: string,
  fetchImpl: FetchLike,
  now: () => number = Date.now,
): Promise<AccessTokenResult> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: config.redirectUri,
    client_id: config.clientId,
    code_verifier: codeVerifier,
  }).toString();

  const response = await fetchImpl(config.tokenEndpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });

  if (!response.ok) throw new TokenExchangeError(response.status);

  const payload = (await response.json()) as TokenEndpointResponse;
  if (!payload.access_token || typeof payload.expires_in !== "number") {
    throw new Error("Respuesta de token incompleta: falta access_token o expires_in");
  }

  const rejectedRefreshToken = typeof payload.refresh_token === "string" && payload.refresh_token.length > 0;
  // payload.refresh_token, si existe, nunca se lee mas alla de esta linea de deteccion.

  return {
    accessToken: payload.access_token,
    expiresAt: now() + payload.expires_in * 1000,
    rejectedRefreshToken,
  };
}
